import { queryOptions, skipToken } from "@tanstack/react-query";

import { MASTER_INVALIDATION_KEY } from "@/api/queryClient";
import {
  getAvailableChecklistTemplates,
  getChecklistTemplate,
  getChecklistTemplates,
  getChecklistTemplatesBySearch,
  getLocalChecklistTemplates,
  getPaginatedChecklistTemplates,
  getPublicChecklistTemplates,
  getSharedChecklistTemplates,
} from "@/api/resources/checklistTemplates/api";
import i18n from "@/providers/i18n";

import type {
  GetChecklistTemplatesBySearchProps,
  GetPaginatedChecklistTemplatesProps,
} from "@/api/resources/checklistTemplates/types";
import type { ArgsGetChecklistTemplate } from "@/supabase.types";

const entity = i18n.t("checklist-template");
const entityErrorMessage = {
  text: i18n.t("notify.error-reason.unexpected"),
  title: i18n.t("notify.get.error", { e: entity }),
};
const entities = i18n.t("checklist-templates");
const entitiesErrorMessage = {
  text: i18n.t("notify.error-reason.unexpected"),
  title: i18n.t("notify.get.error", { e: entities }),
};

export const checklistTemplatesQueryKeys = {
  all: () => [...MASTER_INVALIDATION_KEY, "checklistTemplates"],
  lists: () => [...checklistTemplatesQueryKeys.all(), "list"],
  list: (args: ArgsGetChecklistTemplate) => [...checklistTemplatesQueryKeys.lists(), "list", args],
  listPaginated: (args: GetPaginatedChecklistTemplatesProps) => [...checklistTemplatesQueryKeys.lists(), "list", args],
  listBySearch: (searchOptionFilters: GetChecklistTemplatesBySearchProps) => [
    ...checklistTemplatesQueryKeys.lists(),
    "bySearch",
    searchOptionFilters,
  ],
  listLocal: (workspace_id?: string) => [...checklistTemplatesQueryKeys.lists(), "local", workspace_id],
  listShared: (workspace_id?: string) => [...checklistTemplatesQueryKeys.lists(), "shared", workspace_id],
  listPublic: (workspace_id?: string) => [...checklistTemplatesQueryKeys.lists(), "public", workspace_id],
  listAvailable: (workspace_id?: string) => [...checklistTemplatesQueryKeys.lists(), "available", workspace_id],
  byId: (id?: string) => [...checklistTemplatesQueryKeys.all(), "byId", id],
};

export const checklistTemplatesQueries = {
  list: (args: ArgsGetChecklistTemplate) =>
    queryOptions({
      queryKey: [...checklistTemplatesQueryKeys.list(args)],
      queryFn: () => getChecklistTemplates(args),

      meta: {
        errorMessage: entitiesErrorMessage,
      },
    }),
  listPaginated: (args: GetPaginatedChecklistTemplatesProps) =>
    queryOptions({
      queryKey: [...checklistTemplatesQueryKeys.list(args)],
      queryFn: () => getPaginatedChecklistTemplates(args),

      meta: {
        errorMessage: entitiesErrorMessage,
      },
    }),
  listBySearch: (searchOptionFilters: GetChecklistTemplatesBySearchProps) =>
    queryOptions({
      queryKey: [...checklistTemplatesQueryKeys.listBySearch(searchOptionFilters)],
      queryFn: () => getChecklistTemplatesBySearch(searchOptionFilters),
      placeholderData: (prev) => prev, // ! this prevents flashing of results when using search in selectors
      meta: {
        errorMessage: entitiesErrorMessage,
      },
    }),
  listLocal: (workspace_id?: string) =>
    queryOptions({
      queryKey: [...checklistTemplatesQueryKeys.listLocal(workspace_id)],
      queryFn: workspace_id ? () => getLocalChecklistTemplates(workspace_id) : skipToken,

      meta: {
        errorMessage: entitiesErrorMessage,
      },
    }),
  listShared: (workspace_id?: string) =>
    queryOptions({
      queryKey: [...checklistTemplatesQueryKeys.listShared(workspace_id)],
      queryFn: workspace_id ? () => getSharedChecklistTemplates(workspace_id) : skipToken,

      meta: {
        errorMessage: entitiesErrorMessage,
      },
    }),
  listPublic: (workspace_id?: string) =>
    queryOptions({
      queryKey: [...checklistTemplatesQueryKeys.listPublic(workspace_id)],
      queryFn: workspace_id ? () => getPublicChecklistTemplates(workspace_id) : skipToken,

      meta: {
        errorMessage: entitiesErrorMessage,
      },
    }),
  listAvailable: (workspace_id?: string) =>
    queryOptions({
      queryKey: [...checklistTemplatesQueryKeys.listAvailable(workspace_id)],
      queryFn: workspace_id ? () => getAvailableChecklistTemplates(workspace_id) : skipToken,

      meta: {
        errorMessage: entitiesErrorMessage,
      },
    }),
  byId: (id?: string) =>
    queryOptions({
      queryKey: [...checklistTemplatesQueryKeys.byId(id)],
      queryFn: id ? () => getChecklistTemplate({ id }) : skipToken,

      meta: {
        errorMessage: entityErrorMessage,
      },
    }),
};
